"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  DataTable,
  Panel,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  CheckList,
  type CheckListItem,
} from "@/components/tools/research-writing/check-list";
import { LengthMeter } from "@/components/tools/research-writing/length-meter";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  AIM_PARTS,
  buildSpecificAims,
  type ClaimKind,
  emptyAim,
  MAX_AIMS,
  MIN_AIMS,
  measureSpecificAims,
  type SpecificAim,
  type SpecificAimsInput,
  specificAimsChecks,
} from "@/lib/tool-kit/research-writing/specific-aims";

/** English strings. A translated content file overrides these through `ui`. */
export const SPECIFIC_AIMS_PAGE_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  projectHeading: "Your project",
  titleLabel: "Working title",
  titlePlaceholder: "e.g. Sleep Timing and Glycemic Control in Adolescents",
  pageWordsLabel: "Words that fill one page",
  pageWordsHint:
    "NIH limits pages, not words. 700 is a starting estimate: paste a draft into your own template and enter the figure that fills it.",
  openingHeading: "1. Opening — the problem and the gap",
  hookLabel: "Hook — why the problem matters",
  hookPlaceholder:
    "One or two sentences on the scale or cost of the problem, for a reviewer outside your subfield.",
  knownLabel: "What is known",
  knownPlaceholder:
    "The strongest existing evidence, in two or three sentences. Cite it.",
  gapLabel: "The gap — what is not known",
  gapPlaceholder: "However, it is not known whether … which prevents …",
  objectiveHeading: "2. Objective and central hypothesis",
  longTermLabel: "Long-term goal (optional)",
  longTermPlaceholder: "The long-term goal of this research is to …",
  objectiveLabel: "Objective of this application",
  objectivePlaceholder: "The objective of this application is to …",
  claimKindLabel: "Central claim",
  claimKinds: {
    hypothesis: "A hypothesis",
    question: "A research question",
  },
  claimLabelHypothesis: "Central hypothesis",
  claimLabelQuestion: "Central question",
  claimPlaceholderHypothesis: "Our central hypothesis is that … based on …",
  claimPlaceholderQuestion: "The central question is whether …",
  rationaleLabel: "Rationale or preliminary data (optional)",
  rationalePlaceholder:
    "One sentence that makes the hypothesis credible: your pilot finding or the key prior result.",
  aimsHeading: "3. Specific aims",
  aimsNote:
    "Two to three independent aims is the usual advice for an R01: no aim should depend on another aim succeeding.",
  aimCardLabel: "Aim {n}",
  aimLabel: "The aim — start with an action verb",
  aimPlaceholder: "Determine whether … changes … in …",
  approachLabel: "Approach — design, sample, main measure",
  approachPlaceholder: "We will … in … using …",
  outcomeLabel: "Expected outcome and what a different result means",
  outcomePlaceholder: "We expect … If instead …, then …",
  addAim: "Add an aim",
  removeAim: "Remove aim {n}",
  closingHeading: "4. Payoff — outcomes and impact",
  outcomesLabel: "What the project will deliver",
  outcomesPlaceholder: "These aims will …",
  impactLabel: "Why it matters",
  impactPlaceholder: "The result will change … for … and will lead to …",
  meterHeading: "Page length and checks",
  meterLabel: "Words against one page",
  meterText:
    "one: {words} of {capacity} word|other: {words} of {capacity} words",
  overBy: "Over by {words}",
  leftOver: "{words} left",
  partsHead: {
    part: "Part",
    target: "Target words",
    yours: "Your words",
  },
  partNames: {
    opening: "Opening",
    objective: "Objective and hypothesis",
    aims: "Aims",
    closing: "Payoff",
  },
  checksLabel: "Checklist",
  checkStates: { done: "Done", todo: "To do", note: "Check" },
  checkText: {
    opening_done: "The opening states the problem and the gap.",
    opening_todo: "Add a hook and the gap to the opening.",
    objective_done: "The objective of the application is stated.",
    objective_todo: "State the objective of the application.",
    claim_done: "The central hypothesis or question is stated.",
    claim_todo: "State the central hypothesis or question.",
    aimCount_done: "Two or more aims.",
    aimCount_todo: "Write at least one aim.",
    aimCount_note:
      "Only one aim so far. Most Specific Aims pages carry two or three.",
    outcomes_done: "Every aim has an expected outcome.",
    outcomes_todo: "Add an expected outcome to Aim {aims}.",
    verbs_done: "Every aim opens with a verb that names a result.",
    verbs_note:
      "Aim {aims} opens with a verb that promises activity rather than a result. NIAID's examples of strong verbs include identify, define, quantify, establish and determine.",
    closing_done: "The payoff paragraph says what the project will deliver.",
    closing_todo:
      "Add the payoff: what the project will deliver and why it matters.",
    fit_done: "Within your page estimate.",
    fit_todo: "Write something to measure the length.",
    fit_note:
      "Over your page estimate. Cut before you shrink the font: NIH has minimum type size and margin rules.",
  },
  fileName: "specific-aims",
  document: {
    heading: "Your Specific Aims page",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part has your content.",
  },
  defaultTitle: "Specific Aims",
  subtitle: "Specific Aims page template",
  subtitleWords: "Specific Aims page · {words} words",
  headings: {
    opening: "Opening paragraph",
    objective: "Objective and central hypothesis",
    aims: "Aims",
    closing: "Closing paragraph",
  },
  target: "Target: about {words} words ({share}).",
  tips: {
    opening:
      "Hook, what is known, then the gap — the reviewer should feel the question open up.",
    objective:
      "Say what this application will do and what you expect to find, and why you believe it.",
    aims: "Each aim should stand alone, with its own approach and an outcome that is informative whichever way the result falls.",
    closing:
      "End on the payoff, not on a summary of the aims: what will exist that does not now, and who will use it.",
  },
  prompts: {
    hook: "Open with the problem and its stakes: “[Condition] affects [whom] and leads to [consequence].” Make the first sentence one a reviewer outside your subfield would care about.",
    known:
      "Summarize what is established, citing the strongest evidence: “Studies have shown [finding] [refs].”",
    gap: "Name the gap: “However, it is not known [what is missing], which prevents [consequence].”",
    longTerm:
      "Optional: “The long-term goal of this research is to [broad aim].”",
    objective:
      "State this application's objective: “The objective of this application is to [what you will do or find out].”",
    hypothesis:
      "State the central hypothesis so a reviewer can find it: “Our central hypothesis is that [testable statement].”",
    question:
      "State the central question so a reviewer can find it: “The central question is whether [testable question].”",
    rationale:
      "Add one sentence of rationale or preliminary data: “This is based on [pilot finding or prior result].”",
    aim: "Begin with a strong verb: “Determine whether [X] changes [Y] in [population].”",
    approach:
      "Say how, in one or two sentences: the design, the sample or system, and the main measure.",
    outcome:
      "Say what you expect, and what a different result would mean for the project.",
    outcomes:
      "Say what the project will deliver: “These aims will [produce what].”",
    impact:
      "Say why it matters: “The results will [change what] for [whom] and enable [next step].”",
  },
  aimLead: "Aim {n}.",
  outcomeLead: "Expected outcome:",
  example: {
    title:
      "Sleep Timing and Glycemic Control in Adolescents with Type 1 Diabetes",
    hook: "Many adolescents with type 1 diabetes do not keep their blood glucose within the range clinical guidelines recommend, and each year spent above that range raises the risk of lasting complications.",
    known:
      "Cross-sectional studies link shorter sleep with higher HbA1c in young people with type 1 diabetes [refs], and experimental sleep restriction lowers insulin sensitivity in adults [refs].",
    gap: "However, nearly all of this evidence is a snapshot of one night or one week, so it is not known whether the timing of sleep, rather than its length, predicts glucose control across a school year.",
    longTerm:
      "The long-term goal of this research is to identify behaviors that families and clinicians can change to improve glycemic control in adolescents.",
    objective:
      "The objective of this application is to determine how sleep timing, measured continuously, relates to glucose variability over 12 months.",
    claim:
      "Our central hypothesis is that later and more irregular sleep timing predicts higher glucose variability, independent of sleep duration.",
    rationale:
      "This is based on our pilot data [describe the pilot finding and sample size] and on circadian regulation of insulin sensitivity [refs].",
    outcomes:
      "These aims will yield a year-long, continuously measured account of how sleep timing relates to glucose variability in adolescents with type 1 diabetes.",
    impact:
      "The results will tell clinicians whether to counsel families about when adolescents sleep, not only how long, and will supply the preliminary data for a fully powered intervention trial.",
    aim1: "Determine whether sleep timing predicts glucose variability over 12 months.",
    approach1:
      "We will follow [N] adolescents with wrist actigraphy and continuous glucose monitoring and model within-person changes in timing against glucose variability.",
    outcome1:
      "We expect later, more irregular timing to predict greater variability. If timing adds nothing beyond duration, Aim 2 still identifies which sleep measure matters.",
    aim2: "Quantify how much evening snacking and screen use explain the link between timing and glucose.",
    approach2:
      "Participants will log evening snacks and screen time in a brief daily app, and we will test whether these mediate the association found in Aim 1.",
    outcome2:
      "We expect partial mediation. A null result would point toward the physiological pathways examined in Aim 3.",
    aim3: "Establish the feasibility of a four-week sleep-timing intervention.",
    approach3:
      "In a pilot randomized design, [N] families will follow a fixed-wake-time protocol, and we will measure adherence and retention.",
    outcome3:
      "We expect adherence of at least [target]. The pilot will supply the effect-size estimates a future trial needs.",
  },
} as const;

const EMPTY: SpecificAimsInput = {
  title: "",
  hook: "",
  known: "",
  gap: "",
  longTerm: "",
  objective: "",
  claimKind: "hypothesis",
  claim: "",
  rationale: "",
  aims: [emptyAim("a1"), emptyAim("a2"), emptyAim("a3")],
  outcomes: "",
  impact: "",
  pageWords: "",
};

const CLAIM_KINDS: readonly ClaimKind[] = ["hypothesis", "question"];

type TextKey =
  | "title"
  | "hook"
  | "known"
  | "gap"
  | "longTerm"
  | "objective"
  | "claim"
  | "rationale"
  | "outcomes"
  | "impact"
  | "pageWords";

type AimKey = Exclude<keyof SpecificAim, "id">;

export default function SpecificAimsPageGeneratorWidget() {
  const strings = useToolUi(SPECIFIC_AIMS_PAGE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<SpecificAimsInput>(EMPTY);
  const [nextId, setNextId] = useState(4);
  const doc = useMemo(
    () =>
      buildSpecificAims(
        input,
        {
          ...strings,
          aimLabel: strings.aimLead,
          prompts: strings.prompts,
        },
        fmt
      ),
    [input, strings, fmt]
  );
  const meter = useMemo(() => measureSpecificAims(input), [input]);
  const checks = useMemo(() => specificAimsChecks(input), [input]);
  const documentLabels = useMemo(() => strings.document, [strings]);

  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const updateAim = (id: string, key: AimKey, value: string) =>
    setInput((previous) => ({
      ...previous,
      aims: previous.aims.map((aim) =>
        aim.id === id ? { ...aim, [key]: value } : aim
      ),
    }));
  const addAim = () => {
    setInput((previous) => ({
      ...previous,
      aims: [...previous.aims, emptyAim(`a${nextId}`)],
    }));
    setNextId((value) => value + 1);
  };
  const removeAim = (id: string) =>
    setInput((previous) => ({
      ...previous,
      aims: previous.aims.filter((aim) => aim.id !== id),
    }));
  const loadExample = () => {
    const e = strings.example;
    setInput({
      ...EMPTY,
      title: e.title,
      hook: e.hook,
      known: e.known,
      gap: e.gap,
      longTerm: e.longTerm,
      objective: e.objective,
      claim: e.claim,
      rationale: e.rationale,
      outcomes: e.outcomes,
      impact: e.impact,
      aims: [
        { id: "a1", aim: e.aim1, approach: e.approach1, outcome: e.outcome1 },
        { id: "a2", aim: e.aim2, approach: e.approach2, outcome: e.outcome2 },
        { id: "a3", aim: e.aim3, approach: e.approach3, outcome: e.outcome3 },
      ],
    });
    setNextId(4);
  };
  const clear = () => {
    setInput(EMPTY);
    setNextId(4);
  };

  const claimOptions = CLAIM_KINDS.map((value) => ({
    value,
    label: strings.claimKinds[value],
  }));
  const isHypothesis = input.claimKind === "hypothesis";
  const checkItems: CheckListItem[] = checks.map((check) => ({
    key: check.id,
    state: check.state,
    text: t(
      (strings.checkText as Record<string, string>)[
        `${check.id}_${check.state}`
      ] ?? "",
      { aims: (check.aims ?? []).join(", ") }
    ),
  }));
  const rows = AIM_PARTS.map((part) => [
    strings.partNames[part],
    fmt(meter.targets[part]),
    fmt(meter.parts[part]),
  ]);
  const balance =
    meter.remaining >= 0
      ? t(strings.leftOver, { words: fmt(meter.remaining) })
      : t(strings.overBy, { words: fmt(-meter.remaining) });

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button className={BUTTON_SECONDARY} onClick={clear} type="button">
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.projectHeading}>
        <div className="grid gap-4 sm:grid-cols-[1fr_14rem]">
          <TextField
            id="sa-title"
            label={strings.titleLabel}
            onChange={update("title")}
            placeholder={strings.titlePlaceholder}
            value={input.title}
          />
          <TextField
            hint={strings.pageWordsHint}
            id="sa-page-words"
            label={strings.pageWordsLabel}
            numeric
            onChange={update("pageWords")}
            placeholder={fmt(meter.capacity)}
            value={input.pageWords}
          />
        </div>
      </Panel>

      <Panel title={strings.openingHeading}>
        <TextAreaField
          id="sa-hook"
          label={strings.hookLabel}
          onChange={update("hook")}
          placeholder={strings.hookPlaceholder}
          prose
          rows={2}
          value={input.hook}
        />
        <TextAreaField
          id="sa-known"
          label={strings.knownLabel}
          onChange={update("known")}
          placeholder={strings.knownPlaceholder}
          prose
          rows={3}
          value={input.known}
        />
        <TextAreaField
          id="sa-gap"
          label={strings.gapLabel}
          onChange={update("gap")}
          placeholder={strings.gapPlaceholder}
          prose
          rows={2}
          value={input.gap}
        />
      </Panel>

      <Panel title={strings.objectiveHeading}>
        <TextAreaField
          id="sa-long-term"
          label={strings.longTermLabel}
          onChange={update("longTerm")}
          placeholder={strings.longTermPlaceholder}
          prose
          rows={2}
          value={input.longTerm}
        />
        <TextAreaField
          id="sa-objective"
          label={strings.objectiveLabel}
          onChange={update("objective")}
          placeholder={strings.objectivePlaceholder}
          prose
          rows={2}
          value={input.objective}
        />
        <SelectField
          id="sa-claim-kind"
          label={strings.claimKindLabel}
          onChange={(claimKind) =>
            setInput((previous) => ({ ...previous, claimKind }))
          }
          options={claimOptions}
          value={input.claimKind}
        />
        <TextAreaField
          id="sa-claim"
          label={
            isHypothesis
              ? strings.claimLabelHypothesis
              : strings.claimLabelQuestion
          }
          onChange={update("claim")}
          placeholder={
            isHypothesis
              ? strings.claimPlaceholderHypothesis
              : strings.claimPlaceholderQuestion
          }
          prose
          rows={2}
          value={input.claim}
        />
        <TextAreaField
          id="sa-rationale"
          label={strings.rationaleLabel}
          onChange={update("rationale")}
          placeholder={strings.rationalePlaceholder}
          prose
          rows={2}
          value={input.rationale}
        />
      </Panel>

      <Panel title={strings.aimsHeading}>
        <p className={HINT}>{strings.aimsNote}</p>
        {input.aims.map((aim, index) => (
          <fieldset
            className="space-y-3 rounded-lg border border-border p-4"
            key={aim.id}
          >
            <legend className="px-1 font-medium text-foreground text-sm">
              {t(strings.aimCardLabel, { n: index + 1 })}
            </legend>
            <TextAreaField
              id={`sa-${aim.id}-aim`}
              label={strings.aimLabel}
              onChange={(value) => updateAim(aim.id, "aim", value)}
              placeholder={strings.aimPlaceholder}
              prose
              rows={2}
              value={aim.aim}
            />
            <TextAreaField
              id={`sa-${aim.id}-approach`}
              label={strings.approachLabel}
              onChange={(value) => updateAim(aim.id, "approach", value)}
              placeholder={strings.approachPlaceholder}
              prose
              rows={2}
              value={aim.approach}
            />
            <TextAreaField
              id={`sa-${aim.id}-outcome`}
              label={strings.outcomeLabel}
              onChange={(value) => updateAim(aim.id, "outcome", value)}
              placeholder={strings.outcomePlaceholder}
              prose
              rows={2}
              value={aim.outcome}
            />
            {input.aims.length > MIN_AIMS ? (
              <button
                className={BUTTON_SMALL}
                onClick={() => removeAim(aim.id)}
                type="button"
              >
                {t(strings.removeAim, { n: index + 1 })}
              </button>
            ) : null}
          </fieldset>
        ))}
        <button
          className={BUTTON_SECONDARY}
          disabled={input.aims.length >= MAX_AIMS}
          onClick={addAim}
          type="button"
        >
          {strings.addAim}
        </button>
      </Panel>

      <Panel title={strings.closingHeading}>
        <TextAreaField
          id="sa-outcomes"
          label={strings.outcomesLabel}
          onChange={update("outcomes")}
          placeholder={strings.outcomesPlaceholder}
          prose
          rows={2}
          value={input.outcomes}
        />
        <TextAreaField
          id="sa-impact"
          label={strings.impactLabel}
          onChange={update("impact")}
          placeholder={strings.impactPlaceholder}
          prose
          rows={2}
          value={input.impact}
        />
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.meterHeading}</h2>
        <LengthMeter
          label={strings.meterLabel}
          max={meter.capacity}
          over={meter.remaining < 0}
          value={meter.total}
          valueText={`${tPlural(strings.meterText, locale, meter.capacity, {
            words: fmt(meter.total),
            capacity: fmt(meter.capacity),
          })} · ${balance}`}
        />
        <DataTable
          head={[
            strings.partsHead.part,
            strings.partsHead.target,
            strings.partsHead.yours,
          ]}
          rows={rows}
        />
        <CheckList
          items={checkItems}
          label={strings.checksLabel}
          stateLabels={strings.checkStates}
        />
      </section>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="sa"
        labels={documentLabels}
      />
    </div>
  );
}
